import { useEffect, useRef } from 'react';
import Echo from 'laravel-echo';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import Pusher from 'pusher-js';

import {
  API_URL,
  REVERB_APP_KEY,
  REVERB_HOST,
  REVERB_PORT,
  REVERB_SCHEME,
} from '@/constants/config';
import { tokenStorage } from '@/utils/tokenStorage';

// Thin Laravel Echo wrapper for private channels (chat, signing status).
// Mirrors source/mobile/src/hooks/useWebSocket.ts.

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Pusher?: any;
  }
}

let echoInstance: Echo<'reverb'> | null = null;

function getEcho(): Echo<'reverb'> {
  if (echoInstance) return echoInstance;
  window.Pusher = Pusher;
  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: REVERB_APP_KEY,
    wsHost: REVERB_HOST,
    wsPort: REVERB_PORT,
    wssPort: REVERB_PORT,
    forceTLS: REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${API_URL}/broadcasting/auth`,
    auth: {
      headers: {
        Authorization: `Bearer ${tokenStorage.get() ?? ''}`,
        Accept: 'application/json',
      },
    },
  });
  return echoInstance;
}

/**
 * Подписка на новые сообщения беседы.
 *
 * Канал и событие должны точно совпадать с backend NewChatMessage:
 *   broadcastOn  = PrivateChannel('chat.conversation.{id}')
 *   broadcastAs  = 'new-message'  -> Echo слушает '.new-message' (ведущая точка
 *                  обязательна для кастомного имени события).
 * Раньше веб слушал `lease.{id}` / `.message.created` — оба неверны, realtime
 * не приходил вообще.
 */
export function useConversationChannel(
  conversationId: number | null,
  onMessage: (event: unknown) => void,
): void {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (conversationId === null) return;
    const echo = getEcho();
    const channelName = `chat.conversation.${conversationId}`;
    const channel = echo.private(channelName);
    const listener = (e: unknown) => handlerRef.current(e);
    channel.listen('.new-message', listener);
    return () => {
      try {
        channel.stopListening('.new-message');
        echo.leave(channelName);
      } catch {
        /* ignore */
      }
    };
  }, [conversationId]);
}
